import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Plus, CheckCircle2, Clock, XCircle, AlertCircle, CreditCard, Ban, FileSpreadsheet, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FundRequestsPage() {
  const navigate = useNavigate();
  const { user, hasAnyRole, hasRole } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [approvalData, setApprovalData] = useState({
    approved_amount: '',
    approval_remarks: '',
    expense_closed: false
  });
  const [approving, setApproving] = useState(false);
  
  // Payment states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [phRejectDialogOpen, setPhRejectDialogOpen] = useState(false);
  const [phRejectRemarks, setPhRejectRemarks] = useState('');
  const [processingPhReject, setProcessingPhReject] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: '',
    payment_bank: '',
    payment_voucher_no: '',
    basic_amount: '',
    gst_amount: ''
  });
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [quoteSummary, setQuoteSummary] = useState(null);
  const [loadingQuoteSummary, setLoadingQuoteSummary] = useState(false);
  
  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  // Define action filter options based on user role
  const getActionFilterOptions = () => {
    const options = [{ value: 'all', label: 'All Actions' }];
    
    if (hasAnyRole(['Project Head', 'Super Admin'])) {
      options.push({ value: 'ph_approve', label: 'PH Approve Pending' });
      options.push({ value: 'ph_reject', label: 'PH Reject Pending' });
    }
    
    if (hasRole('Account Manager')) {
      options.push({ value: 'am_approve_payment', label: 'AM Approve Payment' });
      options.push({ value: 'am_reject_payment', label: 'AM Reject Payment' });
    }
    
    options.push({ value: 'no_action', label: 'No Action Required' });
    
    return options;
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/fund-requests`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load fund requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetInfo = async (quoteId) => {
    setLoadingBudget(true);
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/budget`);
      setBudgetInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch budget info');
      setBudgetInfo(null);
    } finally {
      setLoadingBudget(false);
    }
  };

  const fetchQuoteSummary = async (quoteId, quoteNo) => {
    setLoadingQuoteSummary(true);
    try {
      // Fetch quote details
      const quoteResponse = await axios.get(`${API}/quotes/${quoteId}`);
      const quote = quoteResponse.data;
      
      // Fetch budget info to get already approved amount
      const budgetResponse = await axios.get(`${API}/quotes/${quoteId}/budget`);
      const budget = budgetResponse.data;
      
      setQuoteSummary({
        quote_no: quoteNo,
        quotation_amount: quote?.amount_with_management_fee || budget?.total_budget || 0,
        already_approved: budget?.total_approved || 0,
        am_approved_total: budget?.am_approved_total || 0,  // Amount approved by Account Manager
        remaining_amount: (quote?.amount_with_management_fee || budget?.total_budget || 0) - (budget?.total_approved || 0)
      });
    } catch (error) {
      console.error('Failed to fetch quote summary', error);
      setQuoteSummary(null);
    } finally {
      setLoadingQuoteSummary(false);
    }
  };

  const handleOpenApprovalDialog = (request) => {
    setSelectedRequest(request);
    setApprovalData({
      approved_amount: request.requested_amount,
      approval_remarks: '',
      expense_closed: false
    });
    fetchBudgetInfo(request.quote_id);
  };

  const validateApproval = () => {
    const amount = parseFloat(approvalData.approved_amount);
    
    if (!amount || amount <= 0) {
      toast.error('Approve Amount must be greater than zero');
      return false;
    }
    
    if (amount > selectedRequest?.requested_amount) {
      toast.error('Approve Amount cannot exceed Requested Amount');
      return false;
    }
    
    if (!approvalData.approval_remarks.trim()) {
      toast.error('Remarks are mandatory');
      return false;
    }
    
    if (approvalData.approval_remarks.length > 500) {
      toast.error('Remarks must be 500 characters or less');
      return false;
    }
    
    return true;
  };

  const handleApprove = async (requestId) => {
    if (!validateApproval()) return;
    
    setApproving(true);
    try {
      await axios.post(`${API}/fund-requests/${requestId}/approve`, approvalData);
      toast.success('Fund request approved successfully');
      setSelectedRequest(null);
      setBudgetInfo(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  // Payment handlers
  const handleOpenPaymentDialog = (request) => {
    setSelectedRequest(request);
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      payment_bank: '',
      payment_voucher_no: '',
      basic_amount: '',
      gst_amount: ''
    });
    setQuoteSummary(null);
    fetchQuoteSummary(request.quote_id, request.quote_no);
    setPaymentDialogOpen(true);
  };

  const handleOpenRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectRemarks('');
    setRejectDialogOpen(true);
  };

  const handleOpenPhRejectDialog = (request) => {
    setSelectedRequest(request);
    setPhRejectRemarks('');
    setPhRejectDialogOpen(true);
  };

  const handlePhReject = async () => {
    if (!phRejectRemarks.trim()) {
      toast.error('Remarks are mandatory');
      return;
    }
    if (phRejectRemarks.length > 500) {
      toast.error('Remarks must be 500 characters or less');
      return;
    }

    setProcessingPhReject(true);
    try {
      await axios.post(`${API}/fund-requests/${selectedRequest.id}/reject`, { remarks: phRejectRemarks });
      toast.success('Fund request rejected');
      setPhRejectDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject fund request');
    } finally {
      setProcessingPhReject(false);
    }
  };

  const handleApprovePayment = async () => {
    if (!paymentData.payment_date) {
      toast.error('Payment Date is mandatory');
      return;
    }
    if (!paymentData.payment_bank.trim()) {
      toast.error('Payment Bank is mandatory');
      return;
    }
    if (!paymentData.payment_voucher_no.trim()) {
      toast.error('Payment Voucher No. is mandatory');
      return;
    }
    
    const basicAmount = parseFloat(paymentData.basic_amount) || 0;
    const gstAmount = parseFloat(paymentData.gst_amount) || 0;
    const totalAmount = basicAmount + gstAmount;
    
    if (totalAmount <= 0) {
      toast.error('Total Amount must be greater than 0');
      return;
    }
    
    if (totalAmount > selectedRequest?.approved_amount) {
      toast.error(`Total Amount (₹${totalAmount.toLocaleString('en-IN')}) cannot exceed Approved Amount (₹${selectedRequest?.approved_amount?.toLocaleString('en-IN')})`);
      return;
    }

    setProcessingPayment(true);
    try {
      const submitData = {
        ...paymentData,
        basic_amount: basicAmount,
        gst_amount: gstAmount,
        final_amount: totalAmount
      };
      await axios.post(`${API}/fund-requests/${selectedRequest.id}/approve-payment`, submitData);
      toast.success('Payment approved successfully');
      setPaymentDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!rejectRemarks.trim()) {
      toast.error('Remarks are mandatory');
      return;
    }
    if (rejectRemarks.length > 500) {
      toast.error('Remarks must be 500 characters or less');
      return;
    }

    setProcessingPayment(true);
    try {
      await axios.post(`${API}/fund-requests/${selectedRequest.id}/reject-payment`, { remarks: rejectRemarks });
      toast.success('Payment rejected');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: 'default', className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      approved: { variant: 'default', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
      partially_approved: { variant: 'default', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
      rejected: { variant: 'default', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    if (!paymentStatus) return null;
    if (paymentStatus === 'paid') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
          <CreditCard className="h-3 w-3 mr-1" />
          PAID
        </Badge>
      );
    }
    if (paymentStatus === 'rejected') {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          <Ban className="h-3 w-3 mr-1" />
          PAYMENT REJECTED
        </Badge>
      );
    }
    return null;
  };

  const handleExportToExcel = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both From and To dates');
      return;
    }
    
    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date cannot be after To date');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/fund-requests/export?from_date=${exportFromDate}&to_date=${exportToDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fund_Requests_${exportFromDate}_to_${exportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
      setExportDialogOpen(false);
      setExportFromDate('');
      setExportToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Filter requests based on search query and action filter
  const filteredRequests = requests.filter(request => {
    // Search filter
    const matchesSearch = !searchQuery || request.quote_no?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Action filter
    let matchesAction = true;
    if (actionFilter !== 'all') {
      const isPHApprovePending = request.status === 'pending';
      const isAMPaymentPending = (request.status === 'approved' || request.status === 'partially_approved') && !request.payment_status;
      
      switch (actionFilter) {
        case 'ph_approve':
        case 'ph_reject':
          matchesAction = isPHApprovePending;
          break;
        case 'am_approve_payment':
        case 'am_reject_payment':
          matchesAction = isAMPaymentPending;
          break;
        case 'no_action':
          matchesAction = !isPHApprovePending && !isAMPaymentPending;
          break;
        default:
          matchesAction = true;
      }
    }
    
    return matchesSearch && matchesAction;
  });

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading fund requests...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="fund-requests-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fund Requests
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredRequests.length} request(s) found
              {searchQuery && ` (filtered from ${requests.length})`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
            {hasAnyRole(['Site Supervisor', 'Super Admin', 'Project Head']) && (
              <Button onClick={() => navigate('/fund-requests/create')} data-testid="create-fund-request-button">
                <Plus className="mr-2 h-4 w-4" />
                New Fund Request
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filter Box */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by Quote Number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-quote-input"
            />
          </div>
          
          {/* Action Filter - Only show if user has action column */}
          {(hasAnyRole(['Project Head', 'Super Admin']) || hasRole('Account Manager')) && (
            <div className="w-full sm:w-64">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="Filter by Action" />
                </SelectTrigger>
                <SelectContent>
                  {getActionFilterOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Card className="overflow-hidden glass-table">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="fund-requests-table">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-medium">Quote No</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Quotation Date</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Requested By</th>
                  <th className="px-4 py-4 text-right text-sm font-medium">Requested Amount</th>
                  {hasAnyRole(['Project Head', 'Account Manager', 'Super Admin']) && (
                    <th className="px-4 py-4 text-right text-sm font-medium">Approved Amount</th>
                  )}
                  <th className="px-4 py-4 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-4 text-center text-sm font-medium">Actual Approved Amount</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Payment</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Date</th>
                  {(hasAnyRole(['Project Head', 'Super Admin']) || hasRole('Account Manager')) && (
                    <th className="px-4 py-4 text-center text-sm font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-6 py-12 text-center text-muted-foreground">
                      {searchQuery ? `No fund requests found for "${searchQuery}"` : 'No fund requests found'}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-medium">{request.quote_no}</span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {request.quote_date ? new Date(request.quote_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm">{request.requested_by_name}</td>
                      <td className="px-4 py-4 text-sm text-right font-mono">
                        <span className="inline-flex items-center">
                          ₹{request.requested_amount?.toLocaleString('en-IN')}
                          <QuoteCommentsPanel quoteId={request.quote_id} quoteNo={request.quote_no} />
                        </span>
                      </td>
                      {hasAnyRole(['Project Head', 'Account Manager', 'Super Admin']) && (
                        <td className="px-4 py-4 text-sm text-right font-mono">
                          <span className="inline-flex items-center">
                            {request.approved_amount ? `₹${request.approved_amount.toLocaleString('en-IN')}` : '-'}
                            {request.approved_amount && <QuoteCommentsPanel quoteId={request.quote_id} quoteNo={request.quote_no} />}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-4">{getStatusBadge(request.status)}</td>
                      <td className="px-4 py-4">
                        {request.payment_status === 'paid' && (request.basic_amount || request.gst_amount) ? (
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Basic:</span>
                              <span className="font-mono">₹{(request.basic_amount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">GST:</span>
                              <span className="font-mono">₹{(request.gst_amount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1 font-medium">
                              <span className="text-gray-700">Total:</span>
                              <span className="font-mono text-green-700">₹{((request.basic_amount || 0) + (request.gst_amount || 0)).toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">{getPaymentStatusBadge(request.payment_status) || '-'}</td>
                      <td className="px-4 py-4 text-sm">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      {(hasAnyRole(['Project Head', 'Super Admin']) || hasRole('Account Manager')) && (
                        <td className="px-4 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            {/* Project Head Approve Button */}
                            {hasAnyRole(['Project Head', 'Super Admin']) && request.status === 'pending' && (
                              <>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleOpenApprovalDialog(request)}
                                      data-testid={`approve-button-${request.id}`}
                                    >
                                      Approve
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="sm:max-w-lg" data-testid="approval-dialog">
                                    <DialogHeader>
                                      <DialogTitle>Approve Fund Request</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      {/* Budget Information Table */}
                                      <div className="border rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                          <tbody className="divide-y">
                                            <tr className="bg-gray-50">
                                              <td className="px-4 py-3 font-medium text-gray-700">Quote Number</td>
                                              <td className="px-4 py-3 text-right font-mono">{request.quote_no}</td>
                                            </tr>
                                            <tr>
                                              <td className="px-4 py-3 font-medium text-gray-700">Quotation Amount</td>
                                              <td className="px-4 py-3 text-right font-mono">
                                                {loadingBudget ? (
                                                  <span className="text-gray-400">Loading...</span>
                                                ) : (
                                                  `₹${(budgetInfo?.total_budget || 0).toLocaleString('en-IN')}`
                                                )}
                                              </td>
                                            </tr>
                                            <tr className="bg-gray-50">
                                              <td className="px-4 py-3 font-medium text-gray-700">Already Approved Amount</td>
                                              <td className="px-4 py-3 text-right font-mono text-orange-600">
                                                {loadingBudget ? (
                                                  <span className="text-gray-400">Loading...</span>
                                                ) : (
                                                  `₹${(budgetInfo?.total_approved || 0).toLocaleString('en-IN')}`
                                                )}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td className="px-4 py-3 font-medium text-gray-700">Remaining Amount from Quote</td>
                                              <td className="px-4 py-3 text-right font-mono text-green-600">
                                                {loadingBudget ? (
                                                  <span className="text-gray-400">Loading...</span>
                                                ) : (
                                                  `₹${((budgetInfo?.total_budget || 0) - (budgetInfo?.total_approved || 0)).toLocaleString('en-IN')}`
                                                )}
                                              </td>
                                            </tr>
                                            <tr className="bg-gray-50">
                                              <td className="px-4 py-3 font-medium text-gray-700">Requested Amount from Supervisor</td>
                                              <td className="px-4 py-3 text-right font-mono text-blue-600">
                                                ₹{request.requested_amount?.toLocaleString('en-IN')}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Margin Percentage - Calculated field (updates in real-time based on Approve Amount) */}
                                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm font-medium text-blue-700">Margin Percentage</span>
                                          <span className="text-lg font-bold text-blue-800">
                                            {budgetInfo?.total_budget && budgetInfo.total_budget > 0
                                              ? (((parseFloat(budgetInfo.total_budget) - (parseFloat(budgetInfo?.total_approved || 0) + parseFloat(approvalData.approved_amount || 0))) / parseFloat(budgetInfo.total_budget)) * 100).toFixed(2)
                                              : '0.00'}%
                                          </span>
                                        </div>
                                        <p className="text-xs text-blue-600 mt-1">
                                          Formula: [Quotation Amount - (Already Approved + Approve Amount)] / Quotation Amount × 100
                                        </p>
                                      </div>

                                      {/* Approve Amount Input */}
                                      <div className="space-y-2">
                                        <Label htmlFor="approved_amount">Approve Amount (₹) *</Label>
                                        <Input
                                          id="approved_amount"
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max={request.requested_amount}
                                          value={approvalData.approved_amount}
                                          onChange={(e) => setApprovalData({ ...approvalData, approved_amount: e.target.value })}
                                          data-testid="approved-amount-input"
                                          className={parseFloat(approvalData.approved_amount) > request.requested_amount ? 'border-red-500' : ''}
                                        />
                                        {parseFloat(approvalData.approved_amount) > request.requested_amount && (
                                          <p className="text-sm text-red-600 flex items-center gap-1">
                                            <AlertCircle className="h-3 w-3" />
                                            Cannot exceed Requested Amount
                                          </p>
                                        )}
                                      </div>

                                      {/* Remarks Input */}
                                      <div className="space-y-2">
                                        <Label htmlFor="approval_remarks">
                                          Remarks * <span className="text-xs text-muted-foreground">({approvalData.approval_remarks.length}/500)</span>
                                        </Label>
                                        <Textarea
                                          id="approval_remarks"
                                          value={approvalData.approval_remarks}
                                          onChange={(e) => {
                                            if (e.target.value.length <= 500) {
                                              setApprovalData({ ...approvalData, approval_remarks: e.target.value });
                                            }
                                          }}
                                          maxLength={500}
                                          rows={3}
                                          placeholder="Enter approval remarks (mandatory)"
                                          data-testid="approval-remarks-input"
                                        />
                                      </div>

                                      {/* Expense Closed Checkbox */}
                                      <div className="flex items-center space-x-2">
                                        <Checkbox
                                          id="expense_closed"
                                          checked={approvalData.expense_closed}
                                          onCheckedChange={(checked) => setApprovalData({ ...approvalData, expense_closed: checked })}
                                          data-testid="expense-closed-checkbox"
                                        />
                                        <Label htmlFor="expense_closed" className="font-normal">
                                          Mark expense as closed
                                        </Label>
                                      </div>

                                      {/* Confirm Button */}
                                      <Button
                                        onClick={() => handleApprove(request.id)}
                                        disabled={
                                          approving || 
                                          !approvalData.approval_remarks.trim() ||
                                          parseFloat(approvalData.approved_amount) <= 0 ||
                                          parseFloat(approvalData.approved_amount) > request.requested_amount
                                        }
                                        className="w-full"
                                        data-testid="confirm-approve-button"
                                      >
                                        {approving ? 'Approving...' : 'Confirm Approval'}
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleOpenPhRejectDialog(request)}
                                  data-testid={`reject-button-${request.id}`}
                                >
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}

                            {/* Account Manager Payment Buttons */}
                            {hasRole('Account Manager') && 
                             (request.status === 'approved' || request.status === 'partially_approved') && 
                             !request.payment_status && (
                              <>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleOpenPaymentDialog(request)}
                                  data-testid={`approve-payment-button-${request.id}`}
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Approve Payment
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleOpenRejectDialog(request)}
                                  data-testid={`reject-payment-button-${request.id}`}
                                >
                                  <Ban className="h-3 w-3 mr-1" />
                                  Reject Payment
                                </Button>
                              </>
                            )}

                            {/* Show dash if no actions available */}
                            {!(
                              (hasAnyRole(['Project Head', 'Super Admin']) && request.status === 'pending') ||
                              (hasRole('Account Manager') && (request.status === 'approved' || request.status === 'partially_approved') && !request.payment_status)
                            ) && (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Approve Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Approve Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Quote Summary Section */}
            <div className="bg-white rounded-lg border divide-y">
              {loadingQuoteSummary ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading quote details...</div>
              ) : (
                <>
                  <div className="flex justify-between items-center p-3">
                    <span className="text-gray-600">Quote Number</span>
                    <span className="font-semibold text-gray-900">{selectedRequest?.quote_no}</span>
                  </div>
                  <div className="flex justify-between items-center p-3">
                    <span className="text-gray-600">Quotation Amount</span>
                    <span className="font-semibold text-gray-900">₹{quoteSummary?.quotation_amount?.toLocaleString('en-IN') || '0'}</span>
                  </div>
                  <div className="flex justify-between items-center p-3">
                    <span className="text-gray-600">Already Approved Amount</span>
                    <span className="font-semibold text-orange-600">
                      {/* For Account Manager, show AM approved total; for others show PH approved total */}
                      ₹{(hasRole('Account Manager') && !hasRole('Project Head') && !hasRole('Super Admin') 
                        ? quoteSummary?.am_approved_total 
                        : quoteSummary?.already_approved)?.toLocaleString('en-IN') || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3">
                    <span className="text-gray-600">Remaining Amount from Quote</span>
                    <span className="font-semibold text-green-600">
                      {/* For Account Manager, calculate remaining based on AM approved; for others use PH approved */}
                      ₹{(hasRole('Account Manager') && !hasRole('Project Head') && !hasRole('Super Admin')
                        ? (quoteSummary?.quotation_amount - (quoteSummary?.am_approved_total || 0))
                        : quoteSummary?.remaining_amount)?.toLocaleString('en-IN') || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50">
                    <span className="text-gray-700 font-medium">Requested Amount from Supervisor</span>
                    <span className="font-bold text-blue-600">₹{selectedRequest?.requested_amount?.toLocaleString('en-IN') || '0'}</span>
                  </div>
                  {/* Margin Percentage - Different formula for Account Manager */}
                  <div className="flex justify-between items-center p-3 bg-purple-50 border border-purple-200">
                    <div>
                      <span className="text-purple-700 font-medium">Margin Percentage</span>
                      {hasRole('Account Manager') && !hasRole('Project Head') && !hasRole('Super Admin') ? (
                        <p className="text-xs text-purple-500">[Quotation - (Basic + Already Approved)] / (Basic + Already Approved) × 100</p>
                      ) : (
                        <p className="text-xs text-purple-500">(Already Approved + Requested) / Quotation × 100</p>
                      )}
                    </div>
                    <span className="font-bold text-purple-800 text-lg">
                      {hasRole('Account Manager') && !hasRole('Project Head') && !hasRole('Super Admin') ? (
                        // AM Formula: [{Quotation - (Basic Amount + Already Approved)} / (Basic Amount + Already Approved)] × 100
                        (() => {
                          const quotationAmount = parseFloat(quoteSummary?.quotation_amount || 0);
                          const basicAmount = parseFloat(paymentData.basic_amount || 0);
                          const alreadyApproved = parseFloat(quoteSummary?.am_approved_total || 0);
                          const denominator = basicAmount + alreadyApproved;
                          if (denominator > 0) {
                            return (((quotationAmount - denominator) / denominator) * 100).toFixed(2);
                          }
                          return '0.00';
                        })()
                      ) : (
                        // PH/Others Formula: (Already Approved + Requested) / Quotation × 100
                        quoteSummary?.quotation_amount && quoteSummary.quotation_amount > 0
                          ? (((parseFloat(quoteSummary?.already_approved || 0) + parseFloat(selectedRequest?.requested_amount || 0)) / parseFloat(quoteSummary.quotation_amount)) * 100).toFixed(2)
                          : '0.00'
                      )}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50">
                    <span className="text-gray-700 font-medium">Approved Amount (by Project Head)</span>
                    <span className="font-bold text-green-700">₹{selectedRequest?.approved_amount?.toLocaleString('en-IN') || '0'}</span>
                  </div>
                </>
              )}
            </div>

            {/* Amount Breakdown Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <h4 className="text-sm font-semibold text-blue-800">Amount Breakdown</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="basic_amount" className="text-xs">Basic Amount (₹) *</Label>
                  <Input
                    id="basic_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter basic amount"
                    value={paymentData.basic_amount}
                    onChange={(e) => setPaymentData({ ...paymentData, basic_amount: e.target.value })}
                    required
                    data-testid="basic-amount-input"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="gst_amount" className="text-xs">GST Amount (₹) *</Label>
                  <Input
                    id="gst_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter GST amount"
                    value={paymentData.gst_amount}
                    onChange={(e) => setPaymentData({ ...paymentData, gst_amount: e.target.value })}
                    required
                    data-testid="gst-amount-input"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Total Amount (₹)</Label>
                <div className={`p-3 rounded-lg border-2 font-bold text-lg ${
                  ((parseFloat(paymentData.basic_amount) || 0) + (parseFloat(paymentData.gst_amount) || 0)) > (selectedRequest?.approved_amount || 0)
                    ? 'bg-red-100 border-red-400 text-red-700'
                    : 'bg-green-100 border-green-400 text-green-700'
                }`}>
                  ₹{((parseFloat(paymentData.basic_amount) || 0) + (parseFloat(paymentData.gst_amount) || 0)).toLocaleString('en-IN')}
                </div>
                {((parseFloat(paymentData.basic_amount) || 0) + (parseFloat(paymentData.gst_amount) || 0)) > (selectedRequest?.approved_amount || 0) && (
                  <p className="text-xs text-red-600 mt-1">⚠️ Total exceeds Approved Amount!</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                required
                data-testid="payment-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_bank">Payment Bank *</Label>
              <Input
                id="payment_bank"
                type="text"
                placeholder="Enter bank name"
                value={paymentData.payment_bank}
                onChange={(e) => setPaymentData({ ...paymentData, payment_bank: e.target.value })}
                required
                data-testid="payment-bank-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_voucher_no">Payment Voucher No. *</Label>
              <Input
                id="payment_voucher_no"
                type="text"
                placeholder="Enter voucher number"
                value={paymentData.payment_voucher_no}
                onChange={(e) => setPaymentData({ ...paymentData, payment_voucher_no: e.target.value })}
                required
                data-testid="payment-voucher-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleApprovePayment}
                disabled={
                  processingPayment || 
                  !paymentData.payment_date || 
                  !paymentData.payment_bank.trim() || 
                  !paymentData.payment_voucher_no.trim() ||
                  ((parseFloat(paymentData.basic_amount) || 0) + (parseFloat(paymentData.gst_amount) || 0)) <= 0 ||
                  ((parseFloat(paymentData.basic_amount) || 0) + (parseFloat(paymentData.gst_amount) || 0)) > (selectedRequest?.approved_amount || 0)
                }
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-payment-button"
              >
                {processingPayment ? 'Processing...' : 'Confirm Payment'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setPaymentDialogOpen(false)}
                disabled={processingPayment}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Payment Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedRequest?.quote_no}</span></p>
              <p className="text-sm text-gray-600">Approved Amount: <span className="font-medium">₹{selectedRequest?.approved_amount?.toLocaleString('en-IN')}</span></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reject_remarks">
                Remarks * <span className="text-xs text-muted-foreground">({rejectRemarks.length}/500)</span>
              </Label>
              <Textarea
                id="reject_remarks"
                value={rejectRemarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setRejectRemarks(e.target.value);
                  }
                }}
                maxLength={500}
                rows={4}
                placeholder="Enter rejection reason (mandatory)"
                required
                data-testid="reject-remarks-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleRejectPayment}
                disabled={processingPayment || !rejectRemarks.trim()}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-reject-button"
              >
                {processingPayment ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                disabled={processingPayment}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Export Fund Requests to Excel
            </DialogTitle>
            <DialogDescription>
              Select the date range for Quotation Date to export fund requests.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="export_from_date">From Date *</Label>
              <Input
                id="export_from_date"
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="export_to_date">To Date *</Label>
              <Input
                id="export_to_date"
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleExportToExcel}
                disabled={exporting || !exportFromDate || !exportToDate}
                className="flex-1"
              >
                {exporting ? 'Exporting...' : 'Ok'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportDialogOpen(false);
                  setExportFromDate('');
                  setExportToDate('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Project Head Reject Fund Request Dialog */}
      <Dialog open={phRejectDialogOpen} onOpenChange={setPhRejectDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ph-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Fund Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Quote: <span className="font-medium">{selectedRequest?.quote_no}</span></p>
              <p className="text-sm text-gray-600">Requested Amount: <span className="font-medium">₹{selectedRequest?.requested_amount?.toLocaleString('en-IN')}</span></p>
              <p className="text-sm text-gray-600">Requested By: <span className="font-medium">{selectedRequest?.requested_by_name}</span></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ph_reject_remarks">
                Remarks * <span className="text-xs text-muted-foreground">({phRejectRemarks.length}/500)</span>
              </Label>
              <Textarea
                id="ph_reject_remarks"
                value={phRejectRemarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setPhRejectRemarks(e.target.value);
                  }
                }}
                maxLength={500}
                rows={4}
                placeholder="Enter rejection reason (mandatory)"
                required
                data-testid="ph-reject-remarks-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handlePhReject}
                disabled={processingPhReject || !phRejectRemarks.trim()}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-ph-reject-button"
              >
                {processingPhReject ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setPhRejectDialogOpen(false)}
                disabled={processingPhReject}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
